import {useFluxStore} from 'client/framework';
import {addToast, Heading, LoadingSpinner, WindowSizeConsumer} from '@albert-io/atomic';
import type {ClassroomModelV1} from '@albert-io/models';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import kebabCase from 'lodash/kebabCase';
import startCase from 'lodash/startCase';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import appStore from 'client/AppStore';
import constants from 'client/constants';
import sessionStore from 'client/Session/SessionStore';
import type {AdaptiveSubject} from 'client/SubjectLibrary/AdaptiveSubjects.types';
import {AdaptiveSubjects} from 'client/SubjectLibrary/AdaptiveSubjects/AdaptiveSubjects';
import {ALL_SUBJECTS_STRING} from 'client/SubjectLibrary/subjectLibraryConstants';
import type {Map} from 'immutable';
import {List, OrderedMap} from 'immutable';
import useMandarkQueries from 'lib/hooks/useMandarkQueries';
import {normalizeError} from 'lib/normalizeError';
import {normalizeSearchString} from 'lib/stringUtils';

import FreePracticeBanner from './FreePracticeBanner/FreePracticeBanner.react';
import {getActiveClassroomsWithSubjectsQuery} from './helpers/queries';
import SidebarMenu from './SidebarMenu/SidebarMenu';
import SubgroupMenu from './SubgroupMenu/SubgroupMenu.react';
import './subject-menu-renderer.scss';
import SubjectCourseCard from './SubjectCourseCard/SubjectCourseCard';
import {
  getSidebarItemsForMatchingIds,
  getSubjectIdsForPath,
  isLongCardMode,
  sidebarMenuItems
} from './subjectMenuData.utils';
import SubjectMenuHeader from './SubjectMenuHeader/SubjectMenuHeader.react';

import {ADAPTIVE_LABEL} from './constants';
import {getOverriddenDomainString, getOverriddenGroupString} from './stringOverrideHelpers';
import type {SubjectLibrarySubjectsQuery} from './SubjectMenuRenderer.queries.generated';
import {useSubjectLibrarySubjectsPaginated} from './SubjectMenuRenderer.hooks';
import type {SubjectCardData} from './types';

type GQLSubjectResult = NonNullable<
  NonNullable<SubjectLibrarySubjectsQuery['subjects']>['results']
>[number];

/** Map a GQL subject result to a plain SubjectCardData object. */
function toSubjectCardData(gql: GQLSubjectResult): SubjectCardData {
  return {
    id: gql.id,
    name: gql.name,
    description: gql.description ?? '',
    urlSlug: gql.urlSlug,
    curriculumAreaSlug: gql.curriculumArea ? kebabCase(gql.curriculumArea.name) : 'default',
    logoHref: gql.logoImageUrl ? `${constants.SUPPLEMENTS_ORIGIN}/${gql.logoImageUrl}` : ''
  };
}

interface SubjectMenuRendererProps {
  activePath: string[];
  className?: string;
  headerContent?: React.ReactNode;
  headerProps?: Record<string, any>;
  heroBackgroundColor?: string;
  makeSidebarItemHref?: (domain: string, group: string) => string;
  makeSubjectCardContent: (subject: SubjectCardData) => React.ReactNode;
  makeSubjectCardHref?: (subject: SubjectCardData) => string;
  onSearchStringUpdate?: (searchString: string) => void;
  onSidebarItemClick?: (category: string, item: string) => void;
  subgroupMenuContent?: List<Map<string, string>>;
  subjectAreaContent?: React.ReactNode;
  longDirection?: 'normal' | 'reverse';
  subjectCardClassName?: string;
  additionalSidebarMenuItems?: OrderedMap<string, Record<string, any>>;
  isUserLicensedTeacher?: boolean;
  isLoggedOutOrStudent?: boolean;
  subjectBanner?: React.ReactNode;
  adaptiveSubjects?: AdaptiveSubject[];
  canSeeFreeLicensedBadges?: boolean;
}

/**
 * A GraphQL-powered version of SubjectMenuRenderer that replaces the legacy
 * awaitMandarkQueries+subjects_v2 pattern. Instead of fetching ALL subjects
 * upfront, it fetches only the subjects needed for the current navigation path
 * and uses server-side search filtering via the `matchesSearch` calculation.
 */
export function SubjectMenuRenderer({
  activePath,
  className,
  headerContent,
  headerProps,
  heroBackgroundColor = '#399fdf',
  makeSidebarItemHref,
  makeSubjectCardContent,
  makeSubjectCardHref = () => '',
  onSearchStringUpdate = () => {},
  onSidebarItemClick,
  subgroupMenuContent,
  subjectAreaContent,
  longDirection,
  subjectCardClassName,
  additionalSidebarMenuItems = OrderedMap(),
  isUserLicensedTeacher = false,
  isLoggedOutOrStudent = false,
  subjectBanner,
  adaptiveSubjects = [],
  canSeeFreeLicensedBadges = false
}: SubjectMenuRendererProps) {
  const [searchString, setSearchString] = useState('');
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Fetch active classrooms for teachers via Mandark (not yet supported in GQL)
  const isTeacher = useFluxStore(sessionStore, (s) => s.isTeacher());
  const classroomQueries = isTeacher ? [getActiveClassroomsWithSubjectsQuery()] : [];
  const {isReady: classroomsReady, resources: classroomResources} =
    useMandarkQueries(classroomQueries);
  const activeClassrooms = isTeacher && classroomsReady ? classroomResources[0] : List();

  // Cleanup observer on unmount
  useEffect(() => {
    return () => observerRef.current?.disconnect();
  }, []);

  // Callback ref: lazily creates the IntersectionObserver and starts
  // observing as soon as the DOM node appears.
  const headerRef = useCallback((node: HTMLDivElement | null) => {
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setIsHeaderVisible(entry.isIntersecting);
          });
        },
        {threshold: 1}
      );
    }

    observerRef.current.disconnect();
    if (node) {
      observerRef.current.observe(node);
    }
  }, []);

  // Check if the current path is one of the extra sidebar items (Class Subjects, Adaptive Practice)
  const isAdditionalSidebarItem = useCallback(() => {
    return additionalSidebarMenuItems.keySeq().includes(startCase(activePath[0]));
  }, [additionalSidebarMenuItems, activePath]);

  // Determine which subject IDs to fetch based on the current navigation path
  // When searching, always use all subject IDs to get comprehensive results
  const path = isAdditionalSidebarItem() ? [] : activePath;
  const pathSubjectIds = useMemo(() => getSubjectIdsForPath(path), [path.join('/')]);
  const allSubjectIds = useMemo(() => getSubjectIdsForPath([]), []);
  const subjectIds = searchString ? allSubjectIds : pathSubjectIds;

  // Build the GQL filter
  const filter = useMemo(() => {
    const baseFilter: Record<string, any> = {
      hidden: {eq: false},
      published: {eq: true},
      id: {in: subjectIds}
    };

    if (searchString) {
      baseFilter.matchesSearch = {
        input: {search: normalizeSearchString(searchString), searchFields: ['name', 'description']},
        eq: true
      };
    }

    return baseFilter;
  }, [subjectIds, searchString]);

  // Fetch subjects via GQL with keyset pagination — loads pages lazily as user scrolls
  const {
    subjects: rawSubjects,
    loading,
    loadingMore,
    error,
    hasNextPage,
    sentinelRef
  } = useSubjectLibrarySubjectsPaginated({
    filter,
    searchString,
    skip: isAdditionalSidebarItem() && !subjectAreaContent
  });

  useEffect(() => {
    if (error) {
      const appError = normalizeError(error);
      addToast({
        color: 'negative',
        title: 'Something went wrong',
        message: appError.message
      });
    }
  }, [error]);

  // Sort and filter raw GQL subjects, then map to SubjectCardData
  // Sorting is handled server-side (NAME ASC); only client-side filtering remains.
  const activeSubjects: SubjectCardData[] = useMemo(() => {
    let subjects = rawSubjects;

    // Filter out marketplace subjects without pricing
    const {pathname} = appStore.routerProps.location as {pathname: string};
    if (pathname.includes('/marketplace/')) {
      subjects = subjects.filter((s) => (s.subscriptionsPrices ?? []).length > 0);
    }

    return subjects.map(toSubjectCardData);
  }, [rawSubjects]);

  // Pre-compute whether each subject's guides are all assessments
  const assessmentsOnlyMap = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const s of rawSubjects) {
      const guides = s.guides ?? [];
      map[s.id] = guides.length > 0 && guides.every((g) => g.type === 'assessment');
    }
    return map;
  }, [rawSubjects]);

  // Get matching subject IDs for sidebar filtering during search
  const matchingSubjectIds = useMemo(() => {
    if (!searchString) return null;
    return new Set(rawSubjects.map((s) => s.id));
  }, [searchString, rawSubjects]);

  // Sidebar items during search
  const searchSidebarItems = useMemo(() => {
    if (!searchString || !matchingSubjectIds || matchingSubjectIds.size === 0)
      return OrderedMap<string, List<string>>();
    return getSidebarItemsForMatchingIds(matchingSubjectIds);
  }, [searchString, matchingSubjectIds]);

  // Adaptive subjects filtering
  const activeAdaptiveSubjects = useMemo(() => {
    const normalized = searchString ? searchString.toLowerCase() : '';
    const filtered = normalized
      ? adaptiveSubjects.filter((s) =>
          `${s.name} ${s.description || ''}`.toLowerCase().includes(normalized)
        )
      : adaptiveSubjects;

    return [...filtered].sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [adaptiveSubjects, searchString]);

  // oxlint-disable-next-line react-hooks/exhaustive-deps
  const updateSearchString = useCallback(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.trim();
      setSearchString(value);
      onSearchStringUpdate(value);
    }, 250),
    [onSearchStringUpdate]
  );

  const resetSearchString = useCallback(() => {
    setSearchString('');
  }, []);

  function shouldUseLongCards(viewportWidth: number) {
    return isLongCardMode(viewportWidth);
  }

  function getNoSubjectsFoundContent() {
    return searchString ? (
      <div className='subject-cards-wrapper__no-subjects-found'>
        Sorry, your search did not return any subjects.
      </div>
    ) : (
      <div className='subject-cards-wrapper__no-subjects-found'>
        Exciting things are in the works - check back later!
      </div>
    );
  }

  const initialGroup =
    additionalSidebarMenuItems && isAdditionalSidebarItem() ? '' : ALL_SUBJECTS_STRING;

  const [domain = ALL_SUBJECTS_STRING, group = initialGroup, subgroup] = activePath;
  let domainGroupHeadingDetail: string = domain;
  let domainGroupHeadingHeading: string =
    group !== domain
      ? `${getOverriddenDomainString(domain) ?? domain} ${
          getOverriddenGroupString(domain, group) ?? group
        }`
      : domain;

  if (searchString) {
    domainGroupHeadingDetail = `Results in ${
      activePath.length ? activePath.slice(0, 2).join(' – ') : ALL_SUBJECTS_STRING
    }`;
    domainGroupHeadingHeading = `Results for "${searchString}"`;
  }

  const isAdaptiveSubjectsActive = domain === ADAPTIVE_LABEL && !!adaptiveSubjects?.length;

  return (
    <WindowSizeConsumer>
      {({viewportWidth}: {viewportWidth: number}) => (
        <div
          className={classnames(
            'subject-menu-renderer',
            {
              'subject-menu-renderer--with-hero': headerContent
            },
            className
          )}
        >
          {headerContent && (
            <SubjectMenuHeader
              backgroundColor={heroBackgroundColor}
              searchInputProps={{
                onChange: updateSearchString,
                onReset: resetSearchString,
                placeholder: 'Find subjects'
              }}
              {...headerProps}
            >
              {headerContent}
            </SubjectMenuHeader>
          )}
          <div className='subject-menu-renderer__main'>
            <SidebarMenu
              menuItems={searchString ? searchSidebarItems : sidebarMenuItems}
              additionalSidebarMenuItems={additionalSidebarMenuItems}
              makeItemHref={makeSidebarItemHref}
              onItemClick={onSidebarItemClick}
              activePath={activePath}
              group={domainGroupHeadingDetail}
              subgroup={domainGroupHeadingHeading}
              adaptiveSubjects={activeAdaptiveSubjects}
              canSeeFreeLicensedBadges={canSeeFreeLicensedBadges}
            />
            <div className='subject-menu-renderer__subject-area-wrapper'>
              {isTeacher && <FreePracticeBanner />}
              {subjectAreaContent &&
                React.cloneElement(subjectAreaContent as React.ReactElement<any>, {
                  allCourses: (() => {
                    // Deduplicate subjects for ClassSubjects compatibility
                    const ids = new Set<string>();
                    return activeSubjects.filter((s) => {
                      if (ids.has(s.id)) return false;
                      ids.add(s.id);
                      return true;
                    });
                  })(),
                  useLongSubjectCards: shouldUseLongCards(viewportWidth),
                  emptyState: getNoSubjectsFoundContent(),
                  isSearching: !!searchString,
                  searchString,
                  longDirection,
                  makeSubjectCardContent,
                  makeSubjectCardHref,
                  viewportWidth,
                  adaptiveSubjects
                })}
              {!subjectAreaContent && (
                <>
                  <div ref={headerRef}>
                    <Heading className='mt-x2 ml-x5 gap-x1 flex' as='h2'>
                      {getOverriddenDomainString(domain) ?? domain}
                      {group !== domain && (
                        <span className='font-normal'>
                          {getOverriddenGroupString(domain, group) ?? group}
                        </span>
                      )}
                    </Heading>
                  </div>
                  <div
                    className={classnames({
                      'subject-menu-renderer__horizontal-menu': !isHeaderVisible
                    })}
                  >
                    {isHeaderVisible ? null : (
                      <Heading
                        className='subject-menu-renderer__horizontal-menu-header mt-x2'
                        as='h2'
                      >
                        {getOverriddenDomainString(domain) ?? domain}
                        {group !== domain && (
                          <span className='ml-x1 font-normal'>
                            {getOverriddenGroupString(domain, group) ?? group}
                          </span>
                        )}
                      </Heading>
                    )}
                    {subgroupMenuContent && (
                      <SubgroupMenu content={subgroupMenuContent} activeGroup={subgroup} />
                    )}
                  </div>
                  {subjectBanner}
                  {loading && !rawSubjects.length && (
                    <div className='subject-cards-wrapper p-x4 flex justify-center'>
                      <LoadingSpinner />
                    </div>
                  )}
                  {isAdaptiveSubjectsActive ? (
                    <AdaptiveSubjects
                      subjects={activeAdaptiveSubjects}
                      subjectCardClassName={subjectCardClassName}
                      emptyState={getNoSubjectsFoundContent()}
                      isLoggedOutOrStudent={isLoggedOutOrStudent}
                    />
                  ) : null}
                  {!isAdaptiveSubjectsActive && !loading && (
                    <SubjectCardsMapper
                      subjects={activeSubjects}
                      assessmentsOnlyMap={assessmentsOnlyMap}
                      makeSubjectCardContent={makeSubjectCardContent}
                      makeSubjectCardHref={makeSubjectCardHref}
                      useLongSubjectCards={shouldUseLongCards(viewportWidth)}
                      longDirection={longDirection}
                      subjectCardClassName={subjectCardClassName}
                      viewportWidth={viewportWidth}
                      emptyState={getNoSubjectsFoundContent()}
                      isUserLicensedTeacher={isUserLicensedTeacher}
                      isLoggedOutOrStudent={isLoggedOutOrStudent}
                      activeClassrooms={activeClassrooms}
                      loadingMore={loadingMore}
                      hasNextPage={hasNextPage}
                      sentinelRef={sentinelRef}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </WindowSizeConsumer>
  );
}

interface SubjectCardsMapperProps {
  subjects: SubjectCardData[];
  /** Whether all guides for each subject are assessments, keyed by subject ID. */
  assessmentsOnlyMap: Record<string, boolean>;
  makeSubjectCardHref: (subject: SubjectCardData) => string;
  makeSubjectCardContent: (subject: SubjectCardData) => React.ReactNode;
  useLongSubjectCards?: boolean;
  longDirection?: 'normal' | 'reverse';
  subjectCardClassName?: string;
  viewportWidth?: number;
  emptyState?: React.ReactNode;
  isUserLicensedTeacher?: boolean;
  isLoggedOutOrStudent?: boolean;
  activeClassrooms?: List<ClassroomModelV1>;
  loadingMore?: boolean;
  hasNextPage?: boolean;
  sentinelRef?: React.RefObject<HTMLDivElement | null>;
}

function SubjectCardsMapper({
  subjects,
  assessmentsOnlyMap,
  makeSubjectCardHref,
  makeSubjectCardContent,
  subjectCardClassName,
  emptyState,
  isUserLicensedTeacher,
  isLoggedOutOrStudent,
  activeClassrooms,
  loadingMore = false,
  hasNextPage = false,
  sentinelRef
}: SubjectCardsMapperProps) {
  const {pathname} = appStore.routerProps.location as {pathname: string};
  const isSubjectLibrary = !pathname.includes('/marketplace') && pathname.includes('/subjects');

  const content =
    subjects.length === 0
      ? emptyState
      : subjects.map((subject) => {
          if (!isSubjectLibrary) {
            return null;
          }

          return (
            <SubjectCourseCard
              key={subject.id}
              subject={subject}
              to={makeSubjectCardHref(subject)}
              content={makeSubjectCardContent(subject)}
              className={subjectCardClassName}
              isUserLicensedTeacher={isUserLicensedTeacher}
              activeClassrooms={activeClassrooms}
              isLoggedOutOrStudent={isLoggedOutOrStudent}
              isAssessmentsOnly={assessmentsOnlyMap[subject.id] ?? false}
            />
          );
        });

  return (
    <div
      className={classnames('subject-cards-wrapper', {
        'subject-cards-wrapper--course-library': isSubjectLibrary
      })}
    >
      {content}
      {(hasNextPage || loadingMore) && (
        <div ref={sentinelRef} className='p-x4 flex w-full justify-center'>
          {loadingMore && <LoadingSpinner />}
        </div>
      )}
    </div>
  );
}
